import z from 'zod';
const oilTypeEntrySchema = z
    .looseObject({
    /**
     * The oil specification / standard ("MIL-PRF-23699", "AS5780 HPC"), copied as printed. Brain
     * maps no equivalences: "MIL-L-23699" and "MIL-PRF-23699" stay two values. At least one of
     * `specification` and `product` is present.
     */
    specification: z.string().optional(),
    /**
     * The oil product / trade name ("Mobil Jet Oil II"), copied as printed. Brain maps no
     * equivalences: two spellings of one product stay two values, and a product is never mapped to
     * the specification it meets.
     */
    product: z.string().optional(),
    /**
     * The letter marks this oil as the one in use at its date ("currently", "at present"), as
     * distinct from earlier use. Only ever `true` or absent; absent means the letter does not say.
     */
    current: z.literal(true).optional(),
    /**
     * The letter labels this oil an alternate / approved alternative rather than the oil used. Only
     * ever `true` or absent; absent means the letter does not say.
     */
    alternate: z.literal(true).optional(),
    /** Verbatim sentence or table row the oil was read from — the grounding evidence, never empty. */
    quote: z.string(),
    /** Every source page the oil was seen on. */
    pageIds: z.array(z.string()),
})
    .refine((oil) => oil.specification !== undefined || oil.product !== undefined, {
    message: 'an oil names a specification, a product, or both',
});
export const oilTypeEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('oil-type-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Same envelope as `no-exceedance-events-ready`.
    //
    // Required. Brain always emits this stamp. An envelope without it does not parse (DR-3341).
    generatedAt: z.iso.datetime(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    /**
     * EVERY engine oil type statement of the project, not a single winner (DR-6375): an operator
     * letter states the oil the engine was serviced with, so a project holds several. Read from
     * `Engine Oil Type Used Statement`, `NIS Statement` and `PMA/DER Status` letters. One item per
     * letter: a NIS letter is NOT split per stay, so the window is only the letter's own printed
     * coverage. `oils` is always present and never empty: an item with no oil is not sent.
     */
    oilTypeEvents: z.array(z.looseObject({
        /** The Brain document the letter belongs to. */
        documentId: z.string(),
        /** The reconstructed letter the item was read from; one item per letter. */
        reconstructedDocumentId: z.string(),
        /** Every page of the letter, in page order. The pages that stated each oil are in `oils[].pageIds`. */
        pageIds: z.array(z.string()),
        /** The letter's own date, YYYY-MM-DD. Absent when pages disagree or none printed a grounded date. */
        statementDate: z.string().optional(),
        /** First day of the period the letter covers, YYYY-MM-DD. Absent when the letter prints no start. */
        coveragePeriodStart: z.string().optional(),
        /** Last day of the period the letter covers, YYYY-MM-DD. Absent when the letter prints no end. */
        coveragePeriodEnd: z.string().optional(),
        /**
         * The covered period starts when the engine was new ('from New', 'since new', 'since
         * manufacture'); `coveragePeriodStart` is then absent. Only ever `true` or absent.
         */
        coverageSinceNew: z.boolean().optional(),
        /** Company that signed the statement. Absent when pages disagree or none printed a signer. */
        issuerName: z.string().optional(),
        /** `operator` or `camo_on_behalf`. Absent for a shop/lessor/unclear signer, and when pages disagree. */
        issuerRole: z.enum(['operator', 'camo_on_behalf']).optional(),
        /** Engine serials the letter stated, as printed. Brain always emits the array, empty when none. */
        esns: z.array(z.string()),
        /**
         * As on `no-exceedance-events-ready` (DR-6009): `aircraft` means the window is the AIRCRAFT's
         * period and the item counts for an engine only for its stays on that aircraft; absent means engine.
         */
        statementSubject: z.enum(['aircraft', 'engine']).optional(),
        /** Registration of the aircraft the letter names, verbatim. */
        aircraftRegistration: z.string().optional(),
        /** Manufacturer serial number of the aircraft the letter names, verbatim. */
        aircraftMsn: z.string().optional(),
        /** The engine oils the letter states, one per (specification, product) pair. Never empty. */
        oils: z.array(oilTypeEntrySchema).min(1),
    })),
});
//# sourceMappingURL=oil-type-events-ready.webhook.js.map